import { unitRate, type UnitRate } from './money';

/**
 * TCMB BULTENI -> KULLANICI BAKISINDAN KUR SATIRI.
 *
 * ============================ ALIS/SATIS CEVIRIMI ============================
 * Sunucudaki `/v1/rates` vekili BILINCLI olarak bu esleme yapmaz: TCMB'nin
 * alanlarini oldugu gibi gecirir. Cevirimi ISTEMCI yapar ve TEK YERDE yapar —
 * bu dosyada — cunku mobil istemci de ayni cevirimi yapiyor ve iki istemcinin
 * ayni bultenden farkli fiyat gostermesi, kullanicinin asla cozemeyecegi bir
 * hata olurdu.
 *
 * Kural: KULLANICI BANKANIN SATIS FIYATINDAN ALIR.
 *
 *     buy  <- banknoteSelling   (kullanici doviz ALIR; banka SATAR)
 *     sell <- banknoteBuying    (kullanici doviz SATAR; banka ALIR)
 *
 * Bu ters gorunur ve tam da bu yuzden burada yaziyor. Alan adlari BANKANIN
 * bakisiyla adlandirilmis; ekran KULLANICININ bakisini gosterir.
 *
 * `forexBuying/forexSelling` (efektif olmayan) DEGIL `banknote*` kullanilir —
 * mobil ile birebir ayni secim: buro tezgahi nakit doviz konusuyor.
 *
 * ================================= BIRIM ====================================
 * TCMB bazi para birimlerini 100 BIRIM uzerinden kote eder (JPY klasik ornek:
 * `unit: 100`). Fiyati birim'e bolmeden gostermek 100 kat yanlis sayi uretir.
 * Burada her fiyat 1 BIRIME normalize edilir; ham kotasyon birimi satirda
 * `unit` olarak durur ki gerekirse etikette gosterilebilsin.
 * ========================================================================== */

/** Sunucunun `/v1/rates` yanitindaki tek kalem. */
export type RateBulletinItem = {
  code: string;
  name?: string | null;
  nameEn?: string | null;
  /** Kotasyon birimi. TCMB JPY'yi 100 birim uzerinden kote eder. */
  unit?: number | null;
  forexBuying?: number | null;
  forexSelling?: number | null;
  banknoteBuying?: number | null;
  banknoteSelling?: number | null;
  crossRateUsd?: number | null;
  crossRateOther?: number | null;
};

/** `/v1/rates` zarfi. `stale` GIZLENMEZ — ekranda soylenir. */
export type RateBulletin = {
  code?: string;
  message?: string;
  bulletinNo?: string | null;
  bulletinDate?: string | null;
  fetchedAt?: string | null;
  /** TCMB'ye ulasilamadi; bu SON BILINEN bulten. */
  stale: boolean;
  items: RateBulletinItem[];
};

/**
 * Ekranda gosterilen para birimleri — mobil ile BIREBIR ayni liste ve ayni sira.
 * Bulten yuzlerce satir donebilir; buro tezgahinda konusulan dort tanesi bunlar.
 */
export const DISPLAYED_CURRENCY_CODES = ['USD', 'EUR', 'GBP', 'CAD'] as const;
export type DisplayedCurrencyCode = (typeof DISPLAYED_CURRENCY_CODES)[number];

/** Turetilmis capraz satirin gorev tipi kodu (sunucudaki taskType degeri). */
export const CROSS_PAIR_CODE = 'EUR/USD';

export type DisplayRate = {
  /** Liste anahtari. Capraz satirda 'EUR/USD'. */
  key: string;
  /** Talep acilirken sunucuya giden `taskType`. */
  taskTypeCode: string;
  /** 'USD/TRY' gibi gorunur cift etiketi. */
  pairLabel: string;
  /** TCMB'nin verdigi ad (tr). */
  name: string;
  /** Kullanici 1 birimi BU fiyata ALIR (bankanin satisi). */
  buy: UnitRate;
  /** Kullanici 1 birimi BU fiyata SATAR (bankanin alisi). */
  sell: UnitRate;
  /** Fiyatin yazildigi para birimi. */
  quoteCurrency: string;
  /** Fiyatlandirilan para birimi. */
  baseCurrency: string;
  /** TCMB kotasyon birimi (1 ya da 100). Fiyatlar zaten 1 birime normalize edildi. */
  unit: number;
  /** Bultende satiri yok; EUR'nun capraz kurundan TURETILDI. */
  derived: boolean;
};

/** Sonlu ve pozitif mi — TCMB bazi satirlarda bos deger gonderir. */
const usable = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Kotasyonu 1 BIRIME indirger. `unit` yoksa ya da anlamsizsa 1 varsayilir —
 * bolme yapmamak, yanlis bir sayiyla bolmekten daha guvenli.
 */
export const perUnit = (value: number, unit: number | null | undefined): number =>
  usable(unit) ? value / unit : value;

/**
 * EUR satirindan turetilen EUR/USD.
 *
 * `buy = sell = crossRateOther` (1 EUR'nun USD karsiligi). TCMB bu parite icin TEK bir capraz
 * kur yayinlar; alis/satis makasi yok. Eskiden `sell = 1 / crossRateOther` idi: iki sutun
 * farkli BIRIMDE (USD/EUR ve EUR/USD) duruyordu ve ekranda 1,08 / 0,92 gibi bir "makas"
 * gorunuyordu. Teklif ve tutarlar yonden bagimsiz USD/EUR yazildigi icin (bkz.
 * task-currency.ts) iki sutun da ayni birimde. Mobil useExchangeRates ile BIREBIR (2026-09-23).
 */
const crossRow = (eur: RateBulletinItem): DisplayRate | null => {
  const cross = eur.crossRateOther;
  if (!usable(cross)) return null;
  return {
    key: CROSS_PAIR_CODE,
    taskTypeCode: CROSS_PAIR_CODE,
    pairLabel: CROSS_PAIR_CODE,
    name: `${eur.name ?? 'EUR'}/USD`,
    buy: unitRate(cross),
    sell: unitRate(cross),
    quoteCurrency: 'USD',
    baseCurrency: 'EUR',
    unit: 1,
    derived: true,
  };
};

/**
 * Bulteni ekran satirlarina cevirir.
 *
 * Sunucudan gelen SIRA korunur; capraz satir en sona eklenir (mobil de oyle).
 * Banknot fiyati olmayan satir ATLANIR: fiyati olmayan bir kur satiri
 * gostermek, kullaniciya bos bir hucre gostermekten baska bir sey yapmaz.
 */
export const toDisplayRates = (bulletin: Pick<RateBulletin, 'items'>): DisplayRate[] => {
  const wanted = new Set<string>(DISPLAYED_CURRENCY_CODES);
  const rows: DisplayRate[] = [];
  let eur: RateBulletinItem | undefined;

  for (const item of bulletin.items) {
    if (!wanted.has(item.code)) continue;
    if (item.code === 'EUR') eur = item;
    if (!usable(item.banknoteSelling) || !usable(item.banknoteBuying)) continue;

    rows.push({
      key: item.code,
      taskTypeCode: item.code,
      pairLabel: `${item.code}/TRY`,
      name: item.name ?? item.nameEn ?? item.code,
      // Cevirim burada, tek satirda: kullanici bankanin SATIS fiyatindan alir.
      buy: unitRate(perUnit(item.banknoteSelling, item.unit)),
      sell: unitRate(perUnit(item.banknoteBuying, item.unit)),
      quoteCurrency: 'TRY',
      baseCurrency: item.code,
      unit: usable(item.unit) ? item.unit : 1,
      derived: false,
    });
  }

  if (eur) {
    const cross = crossRow(eur);
    if (cross) rows.push(cross);
  }

  return rows;
};
